import { Fragment, Suspense, lazy } from 'react';
import {BrowserRouter,Routes,Route} from 'react-router-dom'
import { ToastContainer } from 'react-toastify';

import FullscreenLoader from './components/MasterLayout/FullscreenLoader';
import LazyLoader from './admin/components/MasterLayout/LazyLoader';

/**
 * Routes are code-split (T-013).
 *
 * Every page already had a <Suspense fallback={<LazyLoader/>}> wrapper inside
 * it, but only ONE of the forty — the dashboard — actually called React.lazy.
 * The other thirty-nine imported their screen eagerly, so those Suspense
 * boundaries had nothing to wait for and every screen in the system was in the
 * first download. Splitting here, at the route, takes the page and everything
 * beneath it out of the initial bundle in one move.
 *
 * The login page and the 404 stay eager on purpose: login is where an
 * unauthenticated visitor lands, and making the first paint wait on a second
 * round trip is the one place this trade does not pay.
 */

import LoginPage from './admin/pages/User/LoginPage';
import Notfound from './admin/components/NotFound/NotFound';

// Every authenticated route is wrapped in this (T-018): a session check that
// redirects to login rather than letting MasterLayout throw on a null user, the
// route's permission looked up by path, and an error boundary so one screen
// failing to render cannot blank the app mid-sale. Eager on purpose — it stands
// in front of every lazy page, so splitting it would only add a round trip
// before the redirect.
import Protected from './auth/Protected';

// Decides which of the two sales order screens /sales/order/create shows, and
// keeps /sales/order/new from stranding someone who has opted out (T-029).
// Eager: it stands in front of a lazy page and only ever renders a redirect or
// its child, so splitting it would buy a round trip and nothing else.
import CounterRoute, { CounterOnlyRoute } from './features/CounterRoute';

// Password recovery: three screens that existed in the tree but had no routes,
// so the "Forget Password" link on the login page led to the Not Found page.
// Lazy like every other page — unlike login itself, nobody lands here first.
const SendOTPPage = lazy(() => import('./admin/pages/User/SendOTPPage'));
const VerifyOTPPage = lazy(() => import('./admin/pages/User/VerifyOTPPage'));
const CreatePasswordPage = lazy(() => import('./admin/pages/User/CreatePasswordPage'));

const DashboardPage = lazy(() => import('./admin/pages/Dashboard/DashboardPage'));
const UserRoleCreateUpdatePage = lazy(() => import('./admin/pages/RolePermission/UserRoleCreateUpdatePage'));
const UserRoleListPage = lazy(() => import('./admin/pages/RolePermission/UserRoleListPage'));
const UserListPage = lazy(() => import('./admin/pages/UserManagement/UserListPage'));
const UserCreateUpdatePage = lazy(() => import('./admin/pages/UserManagement/UserCreateUpdatePage'));
const UserLoginHistoryListPage = lazy(() => import('./admin/pages/UserManagement/UserLoginHistoryListPage'));
const BankCreatePage = lazy(() => import('./admin/pages/Bank/BankCreatePage'));
const BankListPage = lazy(() => import('./admin/pages/Bank/BankListPage'));
const SupplierCreatePage = lazy(() => import('./admin/pages/Supplier/SupplierCreatePage'));
const SupplierListPage = lazy(() => import('./admin/pages/Supplier/SupplierListPage'));
const WarehouseCreatePage = lazy(() => import('./admin/pages/Warehouse/WarehouseCreatePage'));
const WarehouseListPage = lazy(() => import('./admin/pages/Warehouse/WarehouseListPage'));
const MeasurementCreatePage = lazy(() => import('./admin/pages/Measruement/MeasurementCreatePage'));
const MeasurementListPage = lazy(() => import('./admin/pages/Measruement/MeasurementListPage'));
const CategoryCreatePage = lazy(() => import('./admin/pages/Category/CategoryCreatePage'));
const CategoryListPage = lazy(() => import('./admin/pages/Category/CategoryListPage'));
const SubCategoryCreatePage = lazy(() => import('./admin/pages/SubCategory/SubCategoryCreatePage'));
const SubCategoryListPage = lazy(() => import('./admin/pages/SubCategory/SubCategoryListPage'));
const ProductCreatePage = lazy(() => import('./admin/pages/Product/ProductCreatePage'));
const ProductListPage = lazy(() => import('./admin/pages/Product/ProductListPage'));
// Lazy matters more here than on most routes: this screen's chunk leads to the
// PDF writer and the QR encoder, which are lazy AGAIN behind it (see
// ProductQrPrint.jsx). Neither belongs anywhere near the initial download.
const ProductQrPrintPage = lazy(() => import('./admin/pages/Product/ProductQrPrintPage'));
const DiscountListPage = lazy(() => import('./admin/pages/Discount/DiscountListPage'));
const DiscountCreatePage = lazy(() => import('./admin/pages/Discount/DiscountCreatePage'));
const PurchaseOrderCreatePage = lazy(() => import('./admin/pages/Purchase/PurchaseOrderCreatePage'));
const PurchaseOrderListPage = lazy(() => import('./admin/pages/Purchase/PurchaseOrderListPage'));
const PurchaseOrderViewPage = lazy(() => import('./admin/pages/Purchase/PurchaseOrderViewPage'));
const PurchaseOrderRecievePage = lazy(() => import('./admin/pages/Purchase/PurchaseOrderRecievePage'));
const SalesOrderCreatePage = lazy(() => import('./admin/pages/Sales/SalesOrderCreatePage'));
// The Sprint 3 counter, live beside the old screen for three weeks behind the
// newCounter flag (DEVELOPMENT_PLAN.md §9). Lazy like every other page, which
// also means the old screen's bundle costs nothing to anyone who has opted in.
const CounterPage = lazy(() => import('./admin/pages/Sales/CounterPage'));
const SalesOrderListPage = lazy(() => import('./admin/pages/Sales/SalesOrderListPage'));
const SalesOrderViewPage = lazy(() => import('./admin/pages/Sales/SalesOrderViewPage'));
// REQ-3 - editing an already-created invoice.
const SalesOrderEditPage = lazy(() => import('./admin/pages/Sales/SalesOrderEditPage'));
// Sales requests — what erp-mobile sends from the field, and the screen that
// turns one into a real order. Called "requests" here and not "drafts", because
// `src/drafts/` already means browser-local autosave in this codebase.
const SalesRequestListPage = lazy(() => import('./admin/pages/SalesRequest/SalesRequestListPage'));
const SalesRequestConvertPage = lazy(() => import('./admin/pages/SalesRequest/SalesRequestConvertPage'));
const TransactionListPage = lazy(() => import('./admin/pages/Transaction/TransactionListPage'));
// One home for money owed (T-054). The four standalone list routes below stay
// exactly as they are: each is gated on its own permission, each is a link
// people already have, and this screen gates its four tabs the same way rather
// than replacing them.
const DuesPage = lazy(() => import('./admin/pages/Money/DuesPage'));
const CashoutCreatePage = lazy(() => import('./admin/pages/Transaction/CashoutCreatePage'));
const CustomerListPage = lazy(() => import('./admin/pages/Customer/CustomerListPage'));
const CustomerViewPage = lazy(() => import('./admin/pages/Customer/CustomerViewPage'));
const SalesOrderReturnPage = lazy(() => import('./admin/pages/Sales/SalesOrderReturnPage'));
const CreditListPage = lazy(() => import('./admin/pages/Transaction/CreditListPage'));
// BE-27. Expense Categories/Sub-Categories — the screens CreditCategory and
// CreditSubCategory always had backend CRUD for but no route ever used.
const ExpenseCategoryCreatePage = lazy(() => import('./admin/pages/ExpenseCategory/ExpenseCategoryCreatePage'));
const ExpenseCategoryListPage = lazy(() => import('./admin/pages/ExpenseCategory/ExpenseCategoryListPage'));
const ExpenseSubCategoryCreatePage = lazy(() => import('./admin/pages/ExpenseSubCategory/ExpenseSubCategoryCreatePage'));
const ExpenseSubCategoryListPage = lazy(() => import('./admin/pages/ExpenseSubCategory/ExpenseSubCategoryListPage'));
// BE-28. The Expenses section itself — a dedicated create/list pair over
// the same Credit ledger, filtered to is_expense=true.
const ExpenseCreatePage = lazy(() => import('./admin/pages/Expense/ExpenseCreatePage'));
const ExpenseListPage = lazy(() => import('./admin/pages/Expense/ExpenseListPage'));
// BE-31. A second pair of eyes on a large expense.
const ExpenseApprovalListPage = lazy(() => import('./admin/pages/Expense/ExpenseApprovalListPage'));
const ExpenseSettingPage = lazy(() => import('./admin/pages/Expense/ExpenseSettingPage'));
// BE-30. Rent, salary, utilities on a schedule, and the "Post Due Now"
// action while no cron is confirmed in production.
const RecurringExpenseCreatePage = lazy(() => import('./admin/pages/RecurringExpense/RecurringExpenseCreatePage'));
const RecurringExpenseListPage = lazy(() => import('./admin/pages/RecurringExpense/RecurringExpenseListPage'));
const DiscountProductListPage = lazy(() => import('./admin/pages/Discount/DiscountProductListPage'));
const EmployeeSalesTargetListPage = lazy(() => import('./admin/pages/EmployeeSalesTarget/EmployeeSalesTargetListPage'));
const EmployeeSalesTargetCreatePage = lazy(() => import('./admin/pages/EmployeeSalesTarget/EmployeeSalesTargetCreatePage'));
const EmployeeSalesUserListPage = lazy(() => import('./admin/pages/EmployeeSalesTarget/EmployeeSalesUserListPage'));
const EmployeeSalesTargetUserCreatePage = lazy(() => import('./admin/pages/EmployeeSalesTarget/EmployeeSalesTargetUserCreatePage'));

// ---------------------------------------------------------------------------
// Screens the backend backlog unblocked (API_REFERENCE.md, 28 Jul 2026).
//
// None of these could exist before: there was no stock in the API at all
// (BE-01), no exchange document (BE-14), no cheque settlement (B-3), no ageing
// (BE-17) and no per-shop record (BE-03/BE-16).
// ---------------------------------------------------------------------------
const LowStockPage = lazy(() => import('./admin/pages/Stock/LowStockPage'));
const StockValuationPage = lazy(() => import('./admin/pages/Stock/StockValuationPage'));
const OpeningStockPage = lazy(() => import('./admin/pages/Stock/OpeningStockPage'));
const WastagePage = lazy(() => import('./admin/pages/Stock/WastagePage'));
const StockTransferPage = lazy(() => import('./admin/pages/Stock/StockTransferPage'));
const PurchaseReturnPage = lazy(() => import('./admin/pages/Purchase/PurchaseReturnPage'));
const ImportPage = lazy(() => import('./admin/pages/Import/ImportPage'));
const DeliveryChallanPage = lazy(() => import('./admin/pages/Delivery/DeliveryChallanPage'));
const ProfitLossPage = lazy(() => import('./admin/pages/Report/ProfitLossPage'));
const CashbookPage = lazy(() => import('./admin/pages/Report/CashbookPage'));
const ChargesPage = lazy(() => import('./admin/pages/Report/ChargesPage'));
const AuditTrailPage = lazy(() => import('./admin/pages/Audit/AuditTrailPage'));
const ExchangeListPage = lazy(() => import('./admin/pages/Exchange/ExchangeListPage'));
const ExchangeCreatePage = lazy(() => import('./admin/pages/Exchange/ExchangeCreatePage'));
const ExchangeDetailPage = lazy(() => import('./admin/pages/Exchange/ExchangeDetailPage'));
const PendingPaymentsPage = lazy(() => import('./admin/pages/Money/PendingPaymentsPage'));
const AgeingPage = lazy(() => import('./admin/pages/Money/AgeingPage'));
// B-7 - credit terms per class of customer.
const CreditPolicyPage = lazy(() => import('./admin/pages/Customer/CreditPolicyPage'));
// Supplier incentive schemes, and what they have earned.
const SupplierCommissionPage = lazy(() => import('./admin/pages/Supplier/SupplierCommissionPage'));
const ShopSettingsPage = lazy(() => import('./admin/pages/Shop/ShopSettingsPage'));
// REQ-3, decision D-3 - which invoice fields an edit may touch.
const SalesOrderEditableFieldsPage = lazy(() => import('./admin/pages/Settings/SalesOrderEditableFieldsPage'));

// BE-20 - COMPARISON_RULES.md. "Compared to what?" is the owner's real
// question, and until now the app could not answer it at all.
const ComparisonPage = lazy(() => import('./admin/pages/Comparison/ComparisonPage'));

// BE-19 - a salesperson's own target, and the owner's view of everyone's. The
// target feature had a create screen, a list screen and an assign screen, and
// nowhere the person it applies to could see their own number.
const TargetDetailPage = lazy(() => import('./admin/pages/Target/TargetDetailPage'));
const TeamTargetsPage = lazy(() => import('./admin/pages/Target/TeamTargetsPage'));

function App() {

      return(
        <Fragment>
      
        <BrowserRouter>
          <Suspense fallback={<LazyLoader/>}>
          <Routes>
            <Route exact path="/" element={<LoginPage/>} />

            <Route exact path="/login" element={<LoginPage/>} />

            {/* Password recovery, in order. Lowercase to match every other path
                in this file — react-router matches case-sensitively, which is
                why CreatePassword's navigate("/Login") could never have reached
                the login route above. */}
            <Route exact path="/recover/email" element={<SendOTPPage />} />
            <Route exact path="/recover/otp" element={<VerifyOTPPage />} />
            <Route exact path="/recover/password" element={<CreatePasswordPage />} />

            <Route exact path="/dashboard" element={<Protected path="/dashboard"><DashboardPage /></Protected>} />
            <Route exact path="/comparison" element={<Protected path="/comparison"><ComparisonPage /></Protected>} />

            {/* BE-19. The drill-down is deliberately NOT permission-gated in
                this file: the server authorises it by ownership first, so a
                salesperson always reaches their own target, and anybody
                else's needs employeeTarget.overview — which a client-side map
                cannot express and must not pretend to. */}
            <Route exact path="/my/target/:id" element={<Protected path="/my/target/:id"><TargetDetailPage /></Protected>} />
            <Route exact path="/sales/targets" element={<Protected path="/sales/targets"><TeamTargetsPage /></Protected>} />
            <Route exact path="/user/list" element={<Protected path="/user/list"><UserListPage /></Protected>} />
            <Route exact path="/user/create" element={<Protected path="/user/create"><UserCreateUpdatePage /></Protected>} />
            <Route exact path="/user/role/list" element={<Protected path="/user/role/list"><UserRoleListPage /></Protected>} />
            <Route exact path="/user/role/create" element={<Protected path="/user/role/create"><UserRoleCreateUpdatePage /></Protected>} />
            <Route exact path="/user/login/history" element={<Protected path="/user/login/history"><UserLoginHistoryListPage /></Protected>} />
            <Route exact path="/bank/create" element={<Protected path="/bank/create"><BankCreatePage/></Protected>} />
            <Route exact path="/bank/list" element={<Protected path="/bank/list"><BankListPage/></Protected>} />
            <Route exact path="/supplier/create" element={<Protected path="/supplier/create"><SupplierCreatePage/></Protected>} />
            <Route exact path="/supplier/list" element={<Protected path="/supplier/list"><SupplierListPage/></Protected>} />
            <Route exact path="/warehouse/create" element={<Protected path="/warehouse/create"><WarehouseCreatePage/></Protected>} />
            <Route exact path="/warehouse/list" element={<Protected path="/warehouse/list"><WarehouseListPage/></Protected>} />
            <Route exact path="/measurement/create" element={<Protected path="/measurement/create"><MeasurementCreatePage/></Protected>} />
            <Route exact path="/measurement/list" element={<Protected path="/measurement/list"><MeasurementListPage/></Protected>} />
            <Route exact path="/category/create" element={<Protected path="/category/create"><CategoryCreatePage/></Protected>} />
            <Route exact path="/category/list" element={<Protected path="/category/list"><CategoryListPage/></Protected>} />
            <Route exact path="/subcategory/create" element={<Protected path="/subcategory/create"><SubCategoryCreatePage/></Protected>} />
            <Route exact path="/subcategory/list" element={<Protected path="/subcategory/list"><SubCategoryListPage/></Protected>} />
            <Route exact path="/product/create" element={<Protected path="/product/create"><ProductCreatePage/></Protected>} />
            <Route exact path="/product/list" element={<Protected path="/product/list"><ProductListPage/></Protected>} />
            <Route exact path="/product/qr/print" element={<Protected path="/product/qr/print"><ProductQrPrintPage/></Protected>} />
            <Route exact path="/discount/list" element={<Protected path="/discount/list"><DiscountListPage/></Protected>} />
            <Route exact path="/discount/create" element={<Protected path="/discount/create"><DiscountCreatePage/></Protected>} />
            <Route exact path="/discount/product/list" element={<Protected path="/discount/product/list"><DiscountProductListPage/></Protected>} />
            <Route exact path="/purchase/order/create" element={<Protected path="/purchase/order/create"><PurchaseOrderCreatePage/></Protected>} />
            <Route exact path="/purchase/order/list" element={<Protected path="/purchase/order/list"><PurchaseOrderListPage/></Protected>} />
            <Route exact path="/purchase/order/:id" element={<Protected path="/purchase/order/:id"><PurchaseOrderViewPage/></Protected>} />
            <Route exact path="/purchase/order/recieve" element={<Protected path="/purchase/order/recieve"><PurchaseOrderRecievePage/></Protected>} />
            {/* Both counter screens are live at once for three weeks (§9). The
                existing URL keeps working and decides for itself which one to
                show, so no link, bookmark or sidebar entry has to be edited now
                and edited back when the flag is removed. */}
            <Route exact path="/sales/order/create" element={<Protected path="/sales/order/create"><CounterRoute><SalesOrderCreatePage/></CounterRoute></Protected>} />
            <Route exact path="/sales/order/new" element={<Protected path="/sales/order/new"><CounterOnlyRoute><CounterPage/></CounterOnlyRoute></Protected>} />
            <Route exact path="/sales/order/list" element={<Protected path="/sales/order/list"><SalesOrderListPage/></Protected>} />
            <Route exact path="/sales/order/:id" element={<Protected path="/sales/order/:id"><SalesOrderViewPage/></Protected>} />
            {/* REQ-3 - editable only while delivery is not yet complete and
                gated on salesOrder.update, unlike the view route above. */}
            <Route exact path="/sales/order/:id/edit" element={<Protected path="/sales/order/:id/edit"><SalesOrderEditPage/></Protected>} />
            <Route exact path="/sales/order/return/:id/:customerId" element={<Protected path="/sales/order/return/:id/:customerId"><SalesOrderReturnPage/></Protected>} />

            {/* Declared BEFORE /sales/request/:draftNo would be, and kept a
                distinct literal segment, so "list" can never be read as a
                request number. */}
            <Route exact path="/sales/request/list" element={<Protected path="/sales/request/list"><SalesRequestListPage/></Protected>} />
            <Route exact path="/sales/request/:draftNo" element={<Protected path="/sales/request/:draftNo"><SalesRequestConvertPage/></Protected>} />
            <Route exact path="/money/dues" element={<Protected path="/money/dues"><DuesPage/></Protected>} />
            <Route exact path="/transaction/list" element={<Protected path="/transaction/list"><TransactionListPage/></Protected>} />
            <Route exact path="/transaction/credit/create" element={<Protected path="/transaction/credit/create"><CashoutCreatePage/></Protected>} />
            <Route exact path="/credit/list" element={<Protected path="/credit/list"><CreditListPage/></Protected>} />
            <Route exact path="/expense/create" element={<Protected path="/expense/create"><ExpenseCreatePage/></Protected>} />
            <Route exact path="/expense/list" element={<Protected path="/expense/list"><ExpenseListPage/></Protected>} />
            <Route exact path="/expense/approvals" element={<Protected path="/expense/approvals"><ExpenseApprovalListPage/></Protected>} />
            <Route exact path="/expense/setting" element={<Protected path="/expense/setting"><ExpenseSettingPage/></Protected>} />
            <Route exact path="/expense/recurring/create" element={<Protected path="/expense/recurring/create"><RecurringExpenseCreatePage/></Protected>} />
            <Route exact path="/expense/recurring/list" element={<Protected path="/expense/recurring/list"><RecurringExpenseListPage/></Protected>} />
            <Route exact path="/expense/category/create" element={<Protected path="/expense/category/create"><ExpenseCategoryCreatePage/></Protected>} />
            <Route exact path="/expense/category/list" element={<Protected path="/expense/category/list"><ExpenseCategoryListPage/></Protected>} />
            <Route exact path="/expense/subcategory/create" element={<Protected path="/expense/subcategory/create"><ExpenseSubCategoryCreatePage/></Protected>} />
            <Route exact path="/expense/subcategory/list" element={<Protected path="/expense/subcategory/list"><ExpenseSubCategoryListPage/></Protected>} />
            <Route exact path="/customer/list" element={<Protected path="/customer/list"><CustomerListPage/></Protected>} />
            <Route exact path="/customer/:id" element={<Protected path="/customer/:id"><CustomerViewPage/></Protected>} />
            <Route exact path="/employee/sales/target/list" element={<Protected path="/employee/sales/target/list"><EmployeeSalesTargetListPage/></Protected>} />
            <Route exact path="/employee/sales/target/create" element={<Protected path="/employee/sales/target/create"><EmployeeSalesTargetCreatePage/></Protected>} />
            <Route exact path="/employee/sales/target/user/list" element={<Protected path="/employee/sales/target/user/list"><EmployeeSalesUserListPage/></Protected>} />
            <Route exact path="/employee/sales/target/user/create" element={<Protected path="/employee/sales/target/user/create"><EmployeeSalesTargetUserCreatePage/></Protected>} />

            {/* Stock on hand (BE-01, BE-08). Gated on `stock.index`, which is a
                NEW permission — nothing could have named it before, because
                nothing in the API exposed stock. */}
            <Route exact path="/stock/low" element={<Protected path="/stock/low"><LowStockPage/></Protected>} />

            {/* What the godown is worth. A different right from counting
                it: how many boxes are on the shelf is a storekeeper's
                fact, what they cost is the shop's buying position. */}
            <Route exact path="/stock/valuation" element={<Protected path="/stock/valuation"><StockValuationPage/></Protected>} />

            {/* The stock-movement pack (B-5 and friends) — the four ways stock
                moves that are neither a sale nor a purchase.

                Opening and wastage are one screen behind two routes on purpose.
                They are separately permissioned (`stockAdjustment.opening` vs
                `.wastage`), and a storekeeper who may record breakage must not
                thereby be able to restate the godown's opening position — so
                they must be separately reachable, and separately refusable. */}
            <Route exact path="/stock/opening" element={<Protected path="/stock/opening"><OpeningStockPage/></Protected>} />
            <Route exact path="/stock/wastage" element={<Protected path="/stock/wastage"><WastagePage/></Protected>} />
            <Route exact path="/stock/transfer" element={<Protected path="/stock/transfer"><StockTransferPage/></Protected>} />
            <Route exact path="/purchase/return" element={<Protected path="/purchase/return"><PurchaseReturnPage/></Protected>} />

            {/* Bulk import. Gated on `import.store` rather than a read right:
                the preview step writes nothing but will enumerate the
                catalogue a row at a time for any uploaded file. */}
            <Route exact path="/import" element={<Protected path="/import"><ImportPage/></Protected>} />

            {/* Delivery challans — partial handover against an invoice. Takes
                ?so_no=SO-1043 so a salesperson looking at an order can start a
                delivery without hunting for it in a list.

                ⚠️ Nothing on this screen moves stock: stock left the godown's
                books when the invoice was raised, and it stays gone, or
                sold-and-waiting goods would be promised to a second customer. */}
            <Route exact path="/delivery/challan" element={<Protected path="/delivery/challan"><DeliveryChallanPage/></Protected>} />

            {/* The owner's books. Two permissions, not one: the P&L cannot be
                produced without cost of goods, so it discloses the shop's
                buying position; the cashbook shows money in and out and is
                what whoever counts the drawer needs. */}
            <Route exact path="/report/profit-loss" element={<Protected path="/report/profit-loss"><ProfitLossPage/></Protected>} />
            <Route exact path="/report/cashbook" element={<Protected path="/report/cashbook"><CashbookPage/></Protected>} />

            {/* VAT, carrying charge and labour charge — excluded from the P&L
                on purpose (BR-32) and reported here instead, on its own right:
                it discloses no cost of goods, so it is cheap to grant. */}
            <Route exact path="/report/charges" element={<Protected path="/report/charges"><ChargesPage/></Protected>} />

            {/* The audit trail. Its own right, and a narrow one: the trail
                carries the before and after of every price and cost in the
                shop, so it is a way to read the buying position sideways —
                a product's purchase_price history is in there whether or not
                the reader holds `comparison.profit`. */}
            <Route exact path="/audit" element={<Protected path="/audit"><AuditTrailPage/></Protected>} />

            {/* Exchange (BE-14). Listing and creating are separately gated:
                `exchange.index` to look, `exchange.store` to move goods and
                money. */}
            <Route exact path="/exchange/list" element={<Protected path="/exchange/list"><ExchangeListPage/></Protected>} />
            <Route exact path="/exchange/create" element={<Protected path="/exchange/create"><ExchangeCreatePage/></Protected>} />
            {/* Declared after /exchange/create so the literal path keeps
                winning — otherwise "create" is read as an id and the create
                screen becomes an exchange that does not exist. */}
            <Route exact path="/exchange/:id" element={<Protected path="/exchange/:id"><ExchangeDetailPage/></Protected>} />

            {/* Cheque settlement (B-3). `payment.settle` — clearing moves a
                bank balance for real and bouncing reverses an invoice. */}
            <Route exact path="/payment/pending" element={<Protected path="/payment/pending"><PendingPaymentsPage/></Protected>} />

            {/* Receivables ageing (BE-17). */}
            <Route exact path="/customer/ageing" element={<Protected path="/customer/ageing"><AgeingPage/></Protected>} />

            {/* B-7. What each class of customer may owe, set once. */}
            <Route exact path="/customer/credit-policy" element={<Protected path="/customer/credit-policy"><CreditPolicyPage/></Protected>} />

            {/* What the brands pay back, and what breakage is worth claiming. */}
            <Route exact path="/supplier/commission" element={<Protected path="/supplier/commission"><SupplierCommissionPage/></Protected>} />

            {/* The invoice header, per shop (BE-03, BE-16). `shop.update` is an
                owner's right rather than a manager's. */}
            <Route exact path="/shop/settings" element={<Protected path="/shop/settings"><ShopSettingsPage/></Protected>} />
            <Route exact path="/settings/sales-order/editable-fields" element={<Protected path="/settings/sales-order/editable-fields"><SalesOrderEditableFieldsPage/></Protected>} />
            
            <Route path="*" element={<Notfound/>}/>
          </Routes>
          </Suspense>
        </BrowserRouter>
        <FullscreenLoader/>
        <ToastContainer />
      </Fragment>
      )
    
}

export default App;
